import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from "react";

/**
 * Controla o gráfico de continuidade (DEC/FEC) dos conjuntos elétricos.
 *
 * Existem dois pontos de entrada para o mesmo modal: clicar num conjunto no
 * mapa (`openConjunto`) e o botão de gráfico ao lado do nome da camada no
 * painel de Camadas (`openEstado`). O estado mora aqui para que o modal possa
 * ser montado uma única vez, fora da árvore do mapa — assim o gráfico estadual
 * abre mesmo com a camada desmarcada.
 */
export type DgcChartView =
  | { type: "conjunto"; properties: Record<string, any> }
  | { type: "estado" };

interface IDgcChartContext {
  view: DgcChartView | null;
  openConjunto: (properties: Record<string, any>) => void;
  openEstado: () => void;
  close: () => void;
}

const DgcChartContext = createContext<IDgcChartContext>(
  {} as IDgcChartContext
);

export function DgcChartProvider({ children }: { children: ReactNode }) {
  const [view, setView] = useState<DgcChartView | null>(null);

  const openConjunto = useCallback((properties: Record<string, any>) => {
    setView({ type: "conjunto", properties });
  }, []);

  const openEstado = useCallback(() => setView({ type: "estado" }), []);
  const close = useCallback(() => setView(null), []);

  const value = useMemo(
    () => ({ view, openConjunto, openEstado, close }),
    [view, openConjunto, openEstado, close]
  );

  return (
    <DgcChartContext.Provider value={value}>
      {children}
    </DgcChartContext.Provider>
  );
}

export function useDgcChart() {
  return useContext(DgcChartContext);
}
